import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { format } from 'date-fns';
import Button from "../../utils/Button";
import AddIcon from '@mui/icons-material/Add';
import AdminInviteModal from "./AdminInviteModal";
import logo from '../../assets/images/123votelogo.png';
import { useApiQuery } from "../../hooks/useApiQuery";
import { useApiMutation } from "../../hooks/useMutation";
import { ApiRequestOptions } from "../../utils/api";
import { MENU_ITEMS } from "../../utils/Constant";
import DashboardIcon from '@mui/icons-material/Dashboard';
import EventNoteIcon from '@mui/icons-material/EventNote';
import GroupIcon from '@mui/icons-material/Group';
import BarChartIcon from '@mui/icons-material/BarChart';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SettingsIcon from '@mui/icons-material/Settings';
import MessageIcon from '@mui/icons-material/Message';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MenuIcon from '@mui/icons-material/Menu';
import Tooltip from '@mui/material/Tooltip';
import { ShiftProvider } from "../../contexts/ShiftContext";
import { useShift } from "../../hooks/useShift";
import { usePartnerMode } from "../../contexts/PartnerModeContext";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import WebStoriesIcon from '@mui/icons-material/WebStories';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PauseCircleFilledIcon from '@mui/icons-material/PauseCircleFilled';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import BoltIcon from '@mui/icons-material/Bolt';
import RadarIcon from '@mui/icons-material/Radar';
import { AUTH_QUERY_KEY } from "../../utils/auth";
import { resetApplicationSession } from "../../utils/reactQuery";
import { ShiftLocation } from "../../hooks/useShift";
import { BatchPrediction, HourglassBottom, HourglassTop, Start } from "@mui/icons-material";
import { getRoleBasePath, getRoleLoginPath, UserRole } from "../../utils/roleUtils";


const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2);
};

const formatPlan = (plan?: string) => {
  if (!plan) return 'Plan';
  return plan
    .toString()
    .toLowerCase()
    .split('_')
    .map(p => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
};

const iconMap: Record<string, React.ComponentType<any>> = {
  'Overview': DashboardIcon,
  'Analytics': BarChartIcon,
  'Partners': GroupIcon,
  'Settings': SettingsIcon,
  'Manage Canvassers': GroupIcon,
  'Registration Checks': DescriptionIcon,
  'Activity Logs': NotificationsIcon,
  'Audit Logs': WebStoriesIcon,
  'Field Activity': EventNoteIcon,
  'Compliance Logs': DescriptionIcon,
  'Exports': DescriptionIcon,
  'Payments': CreditCardIcon,
  'Messaging Usage': MessageIcon,
  'Billing Usage': AccountBalanceIcon,
  'Shift Overview': ScheduleIcon,
  'Check Registration': CalendarTodayIcon,
  'Assignments': DescriptionIcon,
  'Start Engagement': Start,
  'Batch Requests': BatchPrediction,
  'System Health': MonitorHeartIcon,
};

interface MessagingBillingStatusResponse {
  success: boolean;
  message: string;
  data: {
    profileType: string;
    smsEnabled: boolean;
    brandStatus: string;
    campaignStatus: string;
    setupFeePaid: boolean;
    setupFeeAmount: number;
    setupFeeCheckoutUrl: string | null;
    monthlyComplianceFee: number;
    billingMonth: string;
    smsSegmentsThisMonth: number;
    smsCostThisMonth: number;
    emailCountThisMonth: number;
    emailCostThisMonth: number;
    projectedTotal: number;
  };
}

interface MessagingPayNowResponse {
  success: boolean;
  message: string;
  data: {
    message: string;
    checkoutUrl: string | null;
    reference: string;
  };
}

interface SubscriptionResponse {
  success: boolean;
  message: string;
  data: {
    plan: string;
    status: string;
    nextBillingDate: string;
    lastPaymentAt: string;
    lastPaymentAmount: number;
    stripeCustomerId: string;
    stripeSubscriptionId: string;
    coupon: string | null;
    addons: any[];
    pendingChange?: {
      pendingPlan: string;
      type: 'UPGRADE' | 'DOWNGRADE';
      requestedAt: string;
      effectiveAt: string;
      remainingDays: number;
      scheduled: boolean;
      stripeScheduleId: string;
    };
  };
}

interface PaperFormBatchDraft {
  id: number;
  partnerId: number;
  canvasserId: number;
  claimedForms: number;
  verifiedForms: number;
  rejectedForms: number;
  incompleteForms: number;
  status: string;
  verificationMode: string | null;
  verifiedByUserId: number | null;
  verifiedAt: string | null;
  submittedAt: string | null;
  overdueFlag: boolean;
  hoursToVerify: number | null;
  accuracyScore: number | null;
  rejectionRate: number | null;
  timelinessScore: number | null;
  finalQualityScore: number | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CurrentDraftBatchResponse {
  success: boolean;
  message: string;
  data: PaperFormBatchDraft | null;
}

const getApiErrorMessage = (error: any) => {
  return error?.response?.data?.message || error?.message || '';
};

const isNoDraftBatchError = (error: any) => {
  const message = getApiErrorMessage(error).toLowerCase();
  return (
    error?.response?.status === 404 ||
    message.includes('no draft') ||
    message.includes('draft not found') ||
    message.includes('no current paper form draft')
  );
};

export default function AdminLayout({ children, role }: { children: React.ReactNode, role: string }) {
  const queryClient = useQueryClient();
  const normalizedRole = useMemo(() => role.replace(/\s+/g, "_").toUpperCase(), [role]);
  const typedRole = normalizedRole as UserRole;
  const isSupervisor = normalizedRole === 'SUPERVISOR';
  const isPartnerManagementRole = normalizedRole === 'PARTNER' || normalizedRole === 'PARTNER_ADMIN';
  const canAddCanvasser = normalizedRole === 'PARTNER' || normalizedRole === 'PARTNER_ADMIN' || normalizedRole === 'SUPERVISOR';
  const isPartnerFamilyRole = isPartnerManagementRole || isSupervisor;
  const menuRoleKey = isSupervisor ? 'SUPERVISOR' : isPartnerFamilyRole ? 'PARTNER' : normalizedRole;
  const dashboardBasePath = isPartnerFamilyRole ? getRoleBasePath(typedRole) : '';
  const location = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { mode, setMode } = usePartnerMode();
  const [modeDropdownOpen, setModeDropdownOpen] = useState(false);
  const [mobileUserDropdownOpen, setMobileUserDropdownOpen] = useState(false);
  const [messagingEnabled, setMessagingEnabled] = useState(false);
  const [showMessagingBillingModal, setShowMessagingBillingModal] = useState(false);
  const [hasSeenMessagingBillingModal, setHasSeenMessagingBillingModal] = useState(false);
  const [showCanvasserLogoutModal, setShowCanvasserLogoutModal] = useState(false);
  const [showDraftSubmissionModal, setShowDraftSubmissionModal] = useState(false);
  const [isProcessingLogout, setIsProcessingLogout] = useState(false);
  const [pendingDraftGuardAction, setPendingDraftGuardAction] = useState<'logout-end' | null>(null);
  const [draftBatch, setDraftBatch] = useState<PaperFormBatchDraft | null>(null);
  const [dismissedPendingChanges, setDismissedPendingChanges] = useState<Set<string>>(new Set());
  const [selectedSupervisorId, setSelectedSupervisorId] = useState<number | null>(null);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Partner ID copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const requestOptions: ApiRequestOptions = {
    method: 'GET',
    url: normalizedRole === 'SYSTEM_ADMIN' ? '/admin/me' : isPartnerFamilyRole ? '/partner' : '/user/me',
  };

  const { data: userInfo, isLoading } = useApiQuery<any>({
    queryKey: [normalizedRole, 'me'],
    request: requestOptions,
  });

  const shouldFetchMessagingBilling = isPartnerManagementRole && userInfo?.data?.savedPaymentInstrument === false;
  const {
    data: messagingBillingStatus,
    refetch: refetchMessagingBillingStatus,
  } = useApiQuery<MessagingBillingStatusResponse>({
    queryKey: ['partner', 'messaging', 'billing', 'status'],
    request: {
      method: 'GET',
      url: '/partner/messaging/billing/status',
    },
    enabled: shouldFetchMessagingBilling,
    retry: 1,
  });

  // Fetch subscription details for partners
  const { data: subscriptionData } = useApiQuery<SubscriptionResponse>({
    queryKey: ['partner', 'subscription'],
    request: {
      method: 'GET',
      url: '/partner/admin/subscription',
    },
    enabled: isPartnerManagementRole,
  });

  useEffect(() => {
    const dismissed = localStorage.getItem('dismissedPendingChanges');
    if (dismissed) {
      try {
        setDismissedPendingChanges(new Set(JSON.parse(dismissed)));
      } catch (error) {
        console.error('Failed to parse dismissed pending changes:', error);
      }
    }
  }, []);

  const getPendingChangeKey = (pendingChange: SubscriptionResponse['data']['pendingChange']) => {
    if (!pendingChange) return null;
    return `${pendingChange.pendingPlan}_${pendingChange.type}_${pendingChange.effectiveAt}`;
  };

  const shouldShowPendingChange = () => {
    if (!subscriptionData?.data?.pendingChange) return false;
    const key = getPendingChangeKey(subscriptionData.data.pendingChange);
    return key ? !dismissedPendingChanges.has(key) : true;
  };

  // Handle dismissing pending change notification
  const handleDismissPendingChange = () => {
    if (!subscriptionData?.data?.pendingChange) return;
    const key = getPendingChangeKey(subscriptionData.data.pendingChange);
    if (key) {
      const newDismissed = new Set(dismissedPendingChanges);
      newDismissed.add(key);
      setDismissedPendingChanges(newDismissed);
      localStorage.setItem('dismissedPendingChanges', JSON.stringify(Array.from(newDismissed)));
    }
  };

  const messagingBillingData = messagingBillingStatus?.data;
  const isMessagingBillingModalDisplayWindow = (() => {
    const dayOfMonth = new Date().getDate();
    return dayOfMonth >= 1 && dayOfMonth <= 7;
  })();

  useEffect(() => {
    if (
      !hasSeenMessagingBillingModal && //first condition before it should show the messaging modal
      isMessagingBillingModalDisplayWindow && // show the messaging modal only within the first 7 days of the month
      shouldFetchMessagingBilling && // second condition before it should show the messaging modal
      messagingBillingData?.projectedTotal && // third condition before it should show the messaging modal
      Number(messagingBillingData.projectedTotal) > 1
    ) {
      setShowMessagingBillingModal(true);
      setHasSeenMessagingBillingModal(true);
    }
  }, [hasSeenMessagingBillingModal, isMessagingBillingModalDisplayWindow, messagingBillingData?.projectedTotal, shouldFetchMessagingBilling]);

  useEffect(() => {
    if (userInfo?.data?.enabledModules?.includes('MESSAGING_REMINDER_TOOLS')) {
      setMessagingEnabled(true);
    } else {
      setMessagingEnabled(false);
    }
  }, [userInfo]);

  useEffect(() => {
    const handleMessagingChange = (event: CustomEvent) => {
      setMessagingEnabled(event.detail.enabled);
    };

    window.addEventListener('messagingSettingsChanged', handleMessagingChange as EventListener);

    return () => {
      window.removeEventListener('messagingSettingsChanged', handleMessagingChange as EventListener);
    };
  }, []);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showFreePlanModal, setShowFreePlanModal] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const {
    currentShift,
    isLoading: shiftLoading,
    pauseShift,
    endShift,
  } = useShift();

  const currentDraftBatchMutation = useApiMutation<CurrentDraftBatchResponse>({
    onError: (error: any) => {
      if (isNoDraftBatchError(error)) return;

      const errorMessage = getApiErrorMessage(error) || 'Unable to check current paper form drafts.';
      toast.error(errorMessage);
    },
  });

  const submitDraftBatchMutation = useApiMutation({
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Unable to submit your draft batch.';
      toast.error(errorMessage);
    },
  });

  const { data: supervisorsData, isLoading: supervisorsLoading } = useApiQuery<{
    success: boolean;
    message: string;
    data: Array<{
      id: number;
      fullName: string;
      email: string;
    }>;
  }>({
    queryKey: ['partner', 'supervisors'],
    request: {
      method: 'GET',
      url: '/partner/supervisors',
    },
    enabled: showDraftSubmissionModal,
  });

  useEffect(() => {
    if (isPartnerManagementRole && userInfo?.data?.plan === "FREE") {
      const dontShowFreePlanModal = localStorage.getItem('dontShowFreePlanModal');
      if (!dontShowFreePlanModal) {
        setShowFreePlanModal(true);
        localStorage.setItem('dontShowFreePlanModal', 'true');
      }
    }
  }, [isPartnerManagementRole, userInfo?.data?.plan]);

  const handleCloseFreePlanModal = () => {
    if (dontShowAgain) {
      localStorage.setItem('dontShowFreePlanModal', 'true');
    }
    setShowFreePlanModal(false);
  };

  const handleUpgradeClick = () => {
    if (dontShowAgain) {
      localStorage.setItem('dontShowFreePlanModal', 'true');
    }
    setShowFreePlanModal(false);
    navigate(`${dashboardBasePath}/upgrade`);
  };

  const { mutate: initiateMessagingPayment, isPending: isPayingMessagingBill } = useApiMutation<MessagingPayNowResponse>({
    onSuccess: (response) => {
      const checkoutUrl = response?.data?.checkoutUrl;
      const successMessage = response?.data?.message || response?.message || 'Payment initiated';
      toast.success(successMessage);
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }
      setShowMessagingBillingModal(false);
      refetchMessagingBillingStatus();
    },
    onError: () => {
      toast.error('Failed to initiate messaging payment. Please try again.');
    }
  });

  const handleMessagingPayNow = () => {
    initiateMessagingPayment({
      method: 'POST',
      url: '/partner/billing/sms/pay-now',
    });
  };

  const handleGoToMessagingSettings = () => {
    setShowMessagingBillingModal(false);
    navigate(`${dashboardBasePath}/settings?tab=messaging`);
  };

  const completeLogout = useCallback(() => {
    resetApplicationSession(queryClient);
    navigate(getRoleLoginPath(typedRole));
  }, [navigate, queryClient, typedRole]);

  const getCurrentLocation = useCallback((): Promise<ShiftLocation> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Location services are not available on this device.'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          let locationLabel = 'Current Location';

          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.coords.latitude}&lon=${position.coords.longitude}`
            );
            const result = await response.json();
            locationLabel = result?.display_name || locationLabel;
          } catch (error) {
            console.warn('Failed to resolve location label for logout flow:', error);
          }

          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracyMeters: position.coords.accuracy,
            locationSource: 'GPS',
            locationLabel,
          });
        },
        (error) => {
          reject(new Error(error.message || 'Location permission is required to update your shift before logout.'));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000,
        }
      );
    });
  }, []);

  const performShiftLogout = useCallback(async (action: 'pause' | 'end') => {
    if (!currentShift) {
      completeLogout();
      return;
    }

    setIsProcessingLogout(true);
    const isAlreadyPaused = currentShift.status === 'PAUSED';

    const request = (async () => {
      const location = await getCurrentLocation();

      if (action === 'pause' && currentShift.status === 'ACTIVE') {
        await pauseShift(currentShift.id, location);
      }

      if (action === 'end') {
        await endShift(currentShift.id, location);
      }

      completeLogout();
    })();

    try {
      await toast.promise(request, {
        loading: action === 'pause'
          ? isAlreadyPaused
            ? 'Logging you out with your shift still paused...'
            : 'Pausing shift and logging you out...'
          : 'Ending shift and logging you out...',
        success: action === 'pause'
          ? isAlreadyPaused
            ? 'Paused shift preserved. You have been logged out.'
            : 'Shift paused. You have been logged out.'
          : 'Shift ended. You have been logged out.',
        error: (error) => error?.message || 'Unable to update your shift before logout.',
      });
      setShowCanvasserLogoutModal(false);
    } finally {
      setIsProcessingLogout(false);
    }
  }, [completeLogout, currentShift, endShift, getCurrentLocation, pauseShift]);

  const handleDraftBatchSubmissionForLogout = useCallback(async () => {
    if (!draftBatch || pendingDraftGuardAction !== 'logout-end') return;

    try {
      await toast.promise(
        submitDraftBatchMutation.mutateAsync({
          method: 'POST',
          url: `/paper-form-batches/${draftBatch.id}/submit-draft`,
          data: selectedSupervisorId !== null ? { supervisorId: selectedSupervisorId } : undefined,
        }),
        {
          loading: 'Submitting draft batch to supervisor...',
          success: 'Draft submitted successfully.',
          error: (error: any) => error?.message || 'Unable to submit your draft batch.',
        }
      );

      setShowDraftSubmissionModal(false);
      setShowCanvasserLogoutModal(false);
      setDraftBatch(null);
      setSelectedSupervisorId(null);
      setPendingDraftGuardAction(null);
      await performShiftLogout('end');
    } catch {
      // Toast handled above.
    }
  }, [draftBatch, pendingDraftGuardAction, performShiftLogout, submitDraftBatchMutation, selectedSupervisorId]);

  const handleShiftLogout = useCallback(async (action: 'pause' | 'end') => {
    if (action !== 'end') {
      await performShiftLogout(action);
      return;
    }

    try {
      const response = await currentDraftBatchMutation.mutateAsync({
        method: 'GET',
        url: '/paper-form-batches/current-draft',
      });

      if (response?.data) {
        setDraftBatch(response.data);
        setSelectedSupervisorId(null);
        setPendingDraftGuardAction('logout-end');
        setShowCanvasserLogoutModal(false);
        setShowDraftSubmissionModal(true);
        return;
      }

      toast.success('No drafts found. Proceeding to end shift and log out.');
      await performShiftLogout('end');
    } catch (error) {
      if (isNoDraftBatchError(error)) {
        toast.success('No drafts found. Proceeding to end shift and log out.');
        await performShiftLogout('end');
      }
    }
  }, [currentDraftBatchMutation, performShiftLogout]);

  const handleLogout = useCallback(() => {
    if (normalizedRole === 'CANVASSER' && currentShift && (currentShift.status === 'ACTIVE' || currentShift.status === 'PAUSED')) {
      setShowCanvasserLogoutModal(true);
      return;
    }

    completeLogout();
  }, [completeLogout, currentShift, normalizedRole]);


  return (
    <ShiftProvider currentShift={currentShift} isLoading={shiftLoading}>
      <div className="flex h-screen bg-gray-100 relative">
        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        <button
          className="fixed top-4 right-4 z-50 p-2 bg-white rounded-lg shadow-lg border border-gray-200 lg:hidden"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <CloseIcon className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
        </button>

        {/* Sidebar */}
        <aside className={`${isCollapsed ? 'w-20' : 'w-64'
          } bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out fixed lg:relative h-full z-50 transform ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}>

          {/* Header with Logo and Company Name */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <img src={logo} alt="123 Vote Logo" className="w-10 h-10 flex-shrink-0" />
              {!isCollapsed && (
                <div className="flex flex-col">
                  <span className="text-lg font-bold text-gray-900">123 Vote</span>
                  <span className="text-xs text-gray-500">Voting Management System</span>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Mode Selector - Only visible on mobile */}
          {isPartnerManagementRole && (
            <div className="lg:hidden px-4 pb-4 border-b border-gray-200">
              {/* <label className="block text-sm font-medium text-gray-700 mb-2">Mode</label> */}
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value as 'canvasser' | 'partner_admin')}
                className="w-full px-3 py-2 text-xs rounded-md focus:outline-none focus:border-0"
              >
                <option value="canvasser">Canvasser Mode</option>
                <option value="partner_admin">Partner Admin Mode</option>
              </select>
            </div>
          )}

          {/* Navigation Menu */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <ul className="space-y-1">
              {(MENU_ITEMS as any)[menuRoleKey]?.filter((item: any) => {
                if (isPartnerManagementRole && item.label === 'Registration Checks') {
                  return mode === 'canvasser';
                }
                if (isPartnerManagementRole && item.label === 'Batch Requests') {
                  return mode === 'partner_admin';
                }
                if (isPartnerManagementRole && item.label === 'Manage Canvassers') {
                  // Show for all free plan users but disable click
                  return userInfo?.data?.plan === 'FREE' ? true : mode === 'partner_admin';
                }
                if (isPartnerManagementRole && item.label === 'Field Activity') {
                  // Show for all free plan users but disable click
                  return userInfo?.data?.plan === 'FREE' ? true : mode === 'partner_admin';
                }
                if (isPartnerManagementRole && item.label === 'Exports') {
                  return mode === 'partner_admin';
                }
                if (isPartnerManagementRole && item.label === 'Payments') {
                  return mode === 'partner_admin';
                }
                if (isPartnerManagementRole && item.label === 'Messaging Usage') {
                  return mode === 'partner_admin' && messagingEnabled;
                }
                return true;
              }).map((item: any) => {
                const Icon = iconMap[item.label] || DashboardIcon;
                const isActive = location.pathname === item.to;

                return (
                  <li key={item.to}>
                    {isCollapsed ? (
                      <Tooltip title={item.label} placement="right" arrow>
                        <Link
                          to={item.to}
                          className={`flex items-center justify-center p-3 rounded-lg transition-colors ${isActive
                            ? 'bg-slate-50 text-slate-600 border-l-4 border-slate-600'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <Icon className="w-5 h-5" />
                        </Link>
                      </Tooltip>
                    ) : (
                      <div className="relative">
                        <Link
                          to={userInfo?.data?.plan === 'FREE' && (item.label === 'Manage Canvassers' || item.label === 'Field Activity') ? '#' : item.to}
                          className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${isActive
                            ? 'bg-slate-50 text-slate-600 border-l-4 border-slate-600'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }
                            ${userInfo?.data?.plan === 'FREE' && (item.label === 'Manage Canvassers' || item.label === 'Field Activity') ? "opacity-50 cursor-not-allowed" : ""}
                            `}
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            if (userInfo?.data?.plan === 'FREE' && (item.label === 'Manage Canvassers' || item.label === 'Field Activity')) {
                              setShowFreePlanModal(true);
                            }
                          }}
                        >
                          <Icon className="w-5 h-5 flex-shrink-0" />
                          <span className="font-medium">{item.label}</span>
                        </Link>

                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User Info Section */}
          {!isLoading && userInfo?.data && (
            <div className="px-4 py-3 border-t border-gray-200">
              {!isCollapsed ? (
                <div className="space-y-3">
                  {normalizedRole === 'SYSTEM_ADMIN' ? (
                    /* Admin User Details */
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-xs">
                        {getInitials(userInfo.data.fullName || 'Admin')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{userInfo.data.fullName}</p>
                        <p className="text-xs text-gray-500">System Admin</p>
                      </div>
                    </div>
                  ) : isPartnerFamilyRole ? (
                    /* Partner User Details */
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-slate-500 to-slate-600 rounded-full flex items-center justify-center text-white font-semibold text-xs">
                        {getInitials(userInfo?.data?.name || userInfo?.data?.fullName || 'Partner')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{userInfo?.data?.name || userInfo?.data?.fullName}</p>
                        <p className="text-xs font-semibold text-gray-500">{userInfo?.data?.orgType}</p>
                        {/* <p className="text-xs text-gray-500">{mode === 'canvasser' ? 'Canvasser Mode' : 'Partner Admin'} |  Free</p> */}
                        <p className="text-xs text-gray-500">
                          {formatPlan(userInfo?.data?.plan)}
                          {isPartnerManagementRole && (
                            <>
                              {" | "}
                              <Link className="text-green-700 hover:underline" to={`${dashboardBasePath}/upgrade`}>
                                Upgrade Plan
                              </Link>
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center text-white font-semibold text-xs">
                        {getInitials(userInfo.data.name || userInfo.data.fullName || 'Canvasser')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{userInfo.data.name || userInfo.data.fullName}</p>
                        <p className="text-xs text-gray-500 truncate">{userInfo?.data?.email}</p>
                        <p className="text-xs text-gray-500">Canvasser</p>
                      </div>
                    </div>
                  )}

                  <button
                    className="w-full text-left text-sm text-red-600 hover:text-red-700 hover:bg-red-50 p-2 rounded transition-colors"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Tooltip title="User Profile" placement="right" arrow>
                    <div className="flex justify-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-xs ${normalizedRole === 'SYSTEM_ADMIN'
                        ? 'bg-gradient-to-br from-purple-500 to-purple-600'
                        : isPartnerFamilyRole
                          ? 'bg-gradient-to-br from-slate-500 to-slate-600'
                          : 'bg-gradient-to-br from-green-500 to-green-600'
                        }`}>
                        {getInitials(
                          normalizedRole === 'SYSTEM_ADMIN'
                            ? userInfo.data.fullName || 'Admin'
                            : isPartnerFamilyRole
                              ? userInfo.data.name || userInfo.data.fullName || 'Partner'
                              : userInfo.data.name || userInfo.data.fullName || 'Canvasser'
                        )}
                      </div>

                    </div>
                  </Tooltip>

                  <Tooltip title="Logout" placement="right" arrow>
                    <button
                      className="flex justify-center w-full text-red-600 hover:text-red-700 hover:bg-red-50 p-2 rounded transition-colors"
                      onClick={handleLogout}
                    >
                      <ChevronRightIcon className="w-5 h-5 transform rotate-180" />
                    </button>
                  </Tooltip>
                </div>
              )}
            </div>
          )}

          <div className="p-4 border-t border-gray-200">
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="flex items-center justify-center w-full p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {isCollapsed ? (
                <ChevronRightIcon className="w-5 h-5" />
              ) : (
                <ChevronLeftIcon className="w-5 h-5" />
              )}
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-5 md:px-8 md:py-5 overflow-y-auto lg:ml-0">
          <header className="mb-4 text-lg font-medium text-gray-700 md:flex block justify-between items-center  lg:pt-0">
            <div className="flex flex-col items-start -gap-2">
              <div className="md:flex flex-col items-center gap-2">
                <div className="flex items-center gap-2"><h1>Welcome, {userInfo?.data?.fullName || userInfo?.data?.name || 'User'}</h1>
                  <button
                    onClick={() => setMobileUserDropdownOpen(!mobileUserDropdownOpen)}
                    className="p-1 rounded md:hidden block hover:bg-gray-100 transition-colors"
                  >
                    {mobileUserDropdownOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                  </button>
                </div>
                <div className="md:hidden">

                  {mobileUserDropdownOpen && (
                    <div className="mt-2 w-full">
                      <div className="">
                        {isPartnerFamilyRole && userInfo?.data?.partnerIdentifier && (
                          <div className="flex items-center justify- mb-2">
                            <p className="text-sm text-gray-600">PID: {userInfo?.data?.partnerIdentifier}</p>
                            <Tooltip title="Copy Partner ID" arrow>
                              <button
                                onClick={() => copyToClipboard(userInfo?.data?.partnerIdentifier)}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded hover:bg-gray-100"
                              >
                                <ContentCopyIcon fontSize="small" />
                              </button>
                            </Tooltip>
                          </div>
                        )}
                        {(normalizedRole === "SYSTEM_ADMIN" || canAddCanvasser) && (
                          <Button
                            variant="tertiary"
                            size="large"
                            disabled={isPartnerManagementRole && userInfo?.data?.plan === "FREE"}
                            label={normalizedRole === "SYSTEM_ADMIN" ? "Invite" : isSupervisor ? "Invite Canvasser" : "Invite team"}
                            icon={<AddIcon />}
                            onClick={() => {
                              setShowInviteModal(true);
                              setMobileUserDropdownOpen(false);
                            }}
                            additionalClassname="w-full"
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Desktop PID display - Only visible on desktop */}
              {isPartnerFamilyRole && (
                <div className="hidden md:flex items-center">
                  <p className="text-xs text-gray-500 font-semibold">PID: {userInfo?.data?.partnerIdentifier}</p>
                  {userInfo?.data?.partnerIdentifier && (
                    <Tooltip title="Copy Partner ID" arrow>
                      <button
                        onClick={() => copyToClipboard(userInfo?.data?.partnerIdentifier)}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded hover:bg-gray-100"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </button>
                    </Tooltip>
                  )}
                </div>
              )}
            </div>


            <div className="hidden md:flex gap-2 flex-col sm:flex-row">
              {isPartnerManagementRole && (
                <div className="relative">
                  <Button
                    additionalClassname="hover:border border-slate-500 !px-4 !w-auto"
                    variant="secondary"
                    size="large"
                    label={`${mode === 'canvasser' ? 'Canvasser Mode' : 'Partner Admin Mode'}`}
                    icon={<ArrowDropDownIcon />}
                    onClick={() => setModeDropdownOpen(!modeDropdownOpen)}
                  />
                  {modeDropdownOpen && (
                    <div className="absolute left-0 md:right-0 mt-2  w-full bg-white rounded-md shadow-lg z-50 border border-gray-200">
                      <div className="py-1">
                        <button
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${mode === 'canvasser' ? 'bg-slate-50 text-slate-600' : 'text-gray-700'
                            }`}
                          onClick={() => {
                            setMode('canvasser');
                            setModeDropdownOpen(false);
                          }}
                        >
                          Canvasser Mode
                        </button>
                        <button
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${mode === 'partner_admin' ? 'bg-slate-50 text-slate-600' : 'text-gray-700'
                            }`}
                          onClick={() => {
                            setMode('partner_admin');
                            setModeDropdownOpen(false);
                          }}
                        >
                          Partner Admin Mode
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {(normalizedRole === "SYSTEM_ADMIN" || canAddCanvasser) && (
                <Button
                  additionalClassname={"hover:border border-slate-500 !w-auto px-4"}
                  variant="tertiary"
                  disabled={isPartnerManagementRole && userInfo?.data?.plan === "FREE"}
                  size="large"
                  label={normalizedRole === "SYSTEM_ADMIN" ? "Invite" : isSupervisor ? "Invite Canvasser" : "Invite team"}
                  icon={<AddIcon />}
                  onClick={() => setShowInviteModal(true)}
                />
              )}
            </div>
          </header>
          
          {/* Pending Plan Change Notification */}
          {isPartnerManagementRole && subscriptionData?.data?.pendingChange && shouldShowPendingChange() && (
            <div className={`mb-6 p-4 rounded-xl border relative ${
              subscriptionData.data.pendingChange.type === 'DOWNGRADE' 
                ? 'bg-white bord er-red-200' 
                : 'bg-green-50 border-green-200'
            }`}>
              <button
                onClick={handleDismissPendingChange}
                className={`absolute top-2 right-2 p-1 rounded-full transition-colors ${
                  subscriptionData.data.pendingChange.type === 'DOWNGRADE'
                    ? 'text-red-400 hover:text-red-600 hover:bg-red-100'
                    : 'text-green-400 hover:text-green-600 hover:bg-green-100'
                }`}
                title="Don't show this notification again"
              >
                <CloseIcon className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-3 pr-8">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  subscriptionData.data.pendingChange.type === 'DOWNGRADE'
                    ? 'bg-red-100'
                    : 'bg-green-100'
                }`}>
                  {subscriptionData.data.pendingChange.type === 'DOWNGRADE' ? (
                    <HourglassBottom className="w-5 h-5 text-red-600" />
                  ) : (
                    <HourglassTop className="w-5 h-5 text-green-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className={`font-semibold text-sm ${
                    subscriptionData.data.pendingChange.type === 'DOWNGRADE'
                      ? 'text-red-900'
                      : 'text-green-900'
                  }`}>
                    {subscriptionData.data.pendingChange.type === 'DOWNGRADE' ? 'Downgrade' : 'Upgrade'} Scheduled
                  </h3>
                  <p className={`text-sm mt-1 ${
                    subscriptionData.data.pendingChange.type === 'DOWNGRADE'
                      ? 'text-red-700'
                      : 'text-green-700'
                  }`}>
                    Your plan will change to <span className="font-semibold">
                      {formatPlan(subscriptionData.data.pendingChange.pendingPlan)}
                    </span> in {subscriptionData.data.pendingChange.remainingDays} days 
                    (effective {format(new Date(subscriptionData.data.pendingChange.effectiveAt), 'eeee do MMMM yyyy')})
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {children}
        </main>

        {showMessagingBillingModal && messagingBillingData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full p-6 relative">
              <button
                onClick={() => setShowMessagingBillingModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close messaging billing modal"
              >
                <CloseIcon className="w-5 h-5" />
              </button>

              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <WarningAmberIcon className="text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Manual payment required for messaging</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    You&apos;ve accumulated messaging charges and no saved payment method is available for automated billing.
                    Please settle this month&apos;s balance to keep messaging running without interruption.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Billing month</span>
                  <span className="font-semibold text-gray-900">{messagingBillingData?.billingMonth}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Projected total</span>
                  <span className="font-semibold text-gray-900">${Number(messagingBillingData?.projectedTotal).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Setup fee status</span>
                  <span className="font-semibold text-gray-900">
                    {messagingBillingData?.setupFeePaid ? 'Paid' : 'Not paid'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Brand / campaign</span>
                  <span className="font-semibold text-gray-900">
                    {messagingBillingData?.brandStatus} / {messagingBillingData?.campaignStatus}
                  </span>
                </div>
              </div>

              {!messagingBillingData?.setupFeePaid && Number(messagingBillingData?.projectedTotal) > 1 && (
                <div className="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-100 text-sm text-blue-800">
                  Complete 10DLC registration to enable automated messaging compliance and billing.
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Button
                  variant="primary"
                  size="medium"
                  label={isPayingMessagingBill ? 'Processing...' : 'Pay now'}
                  icon={<CreditCardIcon fontSize="small" />}
                  onClick={handleMessagingPayNow}
                  disabled={isPayingMessagingBill}
                  additionalClassname="flex-1"
                />
                {!messagingBillingData?.setupFeePaid && Number(messagingBillingData?.projectedTotal) > 1 && (
                  <Button
                    variant="secondary"
                    size="medium"
                    label="Go to messaging settings"
                    onClick={handleGoToMessagingSettings}
                    additionalClassname="flex-1"
                  />
                )}
                <Button
                  variant="tertiary"
                  size="medium"
                  label="Maybe later"
                  onClick={() => setShowMessagingBillingModal(false)}
                  additionalClassname="flex-1"
                />
              </div>
            </div>
          </div>
        )}

        {showInviteModal && (
          <AdminInviteModal
            onClose={() => setShowInviteModal(false)}
            role={role}
          />
        )}
        {showCanvasserLogoutModal && currentShift && (
          <div
            className="fixed inset-0 z-[999] flex items-center justify-center p-4"
            aria-modal="true"
            role="dialog"
          >
            <div
              className="absolute inset-0 bg-slate-950/55 backdrop-blur-md"
              onClick={() => {
                if (!isProcessingLogout) {
                  setShowCanvasserLogoutModal(false);
                }
              }}
            />
            <div className="relative w-full max-w-2xl overflow-hidden rounded-[32px] border border-white/70 bg-[linear-gradient(145deg,rgba(255,255,255,0.95),rgba(248,250,252,0.92))] text-slate-900 shadow-[0_32px_120px_rgba(15,23,42,0.32)] backdrop-blur-2xl">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500" />
              <div className="p-7 md:p-8">
                <div className="flex items-start gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[radial-gradient(circle_at_top,rgba(251,191,36,0.4),rgba(249,115,22,0.18))] text-amber-600 shadow-inner">
                    <WarningAmberIcon fontSize="medium" />
                  </div>
                  <div className="flex-1">
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-amber-700">
                        Shift In Progress
                      </span>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${currentShift.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {currentShift.status === 'ACTIVE' ? 'Active Shift' : 'Paused Shift'}
                      </span>
                    </div>
                    <h3 className="text-2xl font-semibold tracking-tight text-slate-950">
                      You are currently canvassing
                    </h3>
                    <p className="mt-3 max-w-xl text-sm leading-6 text-slate-600">
                      Stay logged in while you canvass so your engagements are recorded and correctly tracked. If you need to leave now, pause or end your shift before logging out.
                    </p>
                  </div>
                </div>

                <div className="mt-6 grid gap-3 md:grid-cols-2">
                  <div className="rounded-[24px] border border-white/80 bg-white/70 p-4 shadow-[0_12px_40px_rgba(15,23,42,0.06)] backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-600">
                        <BoltIcon fontSize="small" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Engagement protection</p>
                        <p className="text-xs text-slate-500">Prevent missing records while canvassing.</p>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-[24px] border border-white/80 bg-white/70 p-4 shadow-[0_12px_40px_rgba(15,23,42,0.06)] backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-sky-100 text-sky-700">
                        <RadarIcon fontSize="small" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Shift tracking</p>
                        <p className="text-xs text-slate-500">Pause or end with your latest location attached.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex flex-col gap-3">
                  <Button
                    label="Cancel"
                    variant="tertiary"
                    size="extra-large"
                    onClick={() => setShowCanvasserLogoutModal(false)}
                    disabled={isProcessingLogout}
                    additionalClassname="!h-[52px] !rounded-2xl border border-slate-200 bg-white/70 text-slate-700 hover:bg-white"
                  />
                  <Button
                    label="Pause shift and logout"
                    variant="secondary"
                    size="extra-large"
                    icon={<PauseCircleFilledIcon fontSize="small" />}
                    onClick={() => handleShiftLogout('pause')}
                    disabled={isProcessingLogout}
                    additionalClassname="!h-[56px] !rounded-2xl !border-amber-300 !bg-amber-50 !text-amber-700 hover:!bg-amber-100"
                  />
                  <Button
                    label={currentDraftBatchMutation.isPending ? "Checking drafts..." : "End Shift and log out"}
                    variant="primary"
                    size="extra-large"
                    icon={<StopCircleIcon fontSize="small" />}
                    onClick={() => handleShiftLogout('end')}
                    disabled={isProcessingLogout || currentDraftBatchMutation.isPending || submitDraftBatchMutation.isPending}
                    additionalClassname="!h-[56px] !rounded-2xl !bg-gradient-to-r !from-rose-600 !to-red-600 !text-white shadow-[0_20px_60px_rgba(225,29,72,0.28)] hover:opacity-95"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {showDraftSubmissionModal && draftBatch && (
          <div className="fixed inset-0 z-[1999999] flex items-center justify-center p-4" aria-modal="true" role="dialog">
            <div
              className="absolute inset-0 bg-slate-950/55 backdrop-blur-md"
              onClick={() => {
                if (!submitDraftBatchMutation.isPending && !isProcessingLogout) {
                  setShowDraftSubmissionModal(false);
                  setPendingDraftGuardAction(null);
                  setDraftBatch(null);
                }
              }}
            />
            <div className="relative w-full max-w-xl overflow-hidden rounded-[32px] border border-white/70 bg-[linear-gradient(145deg,rgba(255,253,247,0.97),rgba(255,255,255,0.94))] text-slate-900 shadow-[0_32px_120px_rgba(15,23,42,0.32)] backdrop-blur-2xl">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500" />
              <div className="p-7 md:p-8">
                <div className="flex items-start gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[radial-gradient(circle_at_top,rgba(251,191,36,0.4),rgba(249,115,22,0.18))] text-amber-600 shadow-inner">
                    <WarningAmberIcon fontSize="medium" />
                  </div>
                  <div className="flex-1">
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-amber-700">
                        Draft Submission Needed
                      </span>
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                        Batch #{draftBatch.id}
                      </span>
                    </div>
                    <h3 className="text-2xl font-semibold tracking-tight text-slate-950">
                      Submit your paper-form draft before ending shift
                    </h3>
                    <p className="mt-3 text-sm leading-6 text-slate-600">
                      You still have paper-form registrations saved in a draft batch. Before you can end this shift and log out, submit the draft to your supervisor.
                    </p>
                    <p className="mt-3 text-sm leading-6 text-slate-600">
                      Drafts are submitted once a day at the end of your daily shift. If you are not done for the day, pause your shift instead of ending it.
                    </p>
                  </div>
                </div>

                <div className="mt-6 rounded-[24px] border border-amber-100 bg-amber-50/70 p-5">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Current draft batch</p>
                      <p className="mt-1 text-xs text-slate-500">Forms currently waiting in draft status</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-semibold text-amber-700">{draftBatch.claimedForms}</p>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Claimed forms</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Select Supervisor
                  </label>
                  {supervisorsLoading ? (
                    <div className="w-full px-3 py-2 border border-slate-200 rounded-xl bg-slate-50 text-sm">
                      Loading supervisors...
                    </div>
                  ) : supervisorsData?.data && supervisorsData.data.length > 0 ? (
                    <select
                      value={selectedSupervisorId || ''}
                      onChange={(e) => setSelectedSupervisorId(e.target.value ? Number(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm bg-white"
                    >
                      {supervisorsData.data.length > 1 && (
                        <option value="">Select supervisor</option>
                      )}
                      {supervisorsData.data.map((supervisor) => (
                        <option key={supervisor.id} value={supervisor.id}>
                          {supervisor.fullName} ({supervisor.email})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="w-full px-3 py-2 border border-slate-200 rounded-xl bg-slate-50 text-sm text-slate-500">
                      No supervisors available
                    </div>
                  )}
                </div>

                <div className="mt-8 flex flex-col gap-3">
                  <Button
                    label="Go back"
                    variant="tertiary"
                    size="extra-large"
                    onClick={() => {
                      setShowDraftSubmissionModal(false);
                      setPendingDraftGuardAction(null);
                      setDraftBatch(null);
                      setSelectedSupervisorId(null);
                      setShowCanvasserLogoutModal(true);
                    }}
                    disabled={submitDraftBatchMutation.isPending || isProcessingLogout}
                    additionalClassname="!h-[52px] !rounded-2xl border border-slate-200 bg-white/70 text-slate-700 hover:bg-white"
                  />
                  <Button
                    label={submitDraftBatchMutation.isPending ? "Submitting draft..." : "Submit draft to supervisor"}
                    variant="primary"
                    size="extra-large"
                    icon={<BoltIcon fontSize="small" />}
                    onClick={handleDraftBatchSubmissionForLogout}
                    disabled={submitDraftBatchMutation.isPending || isProcessingLogout || (supervisorsData?.data && supervisorsData.data.length > 1 && !selectedSupervisorId)}
                    additionalClassname="!h-[56px] !rounded-2xl !bg-gradient-to-r !from-amber-500 !to-orange-500 !text-white shadow-[0_20px_60px_rgba(245,158,11,0.28)] hover:opacity-95"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {showFreePlanModal && isPartnerManagementRole && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-3xl  w-full p-6 md:px-10 relative">
              <button
                onClick={handleCloseFreePlanModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <CloseIcon className="w-5 h-5" />
              </button>

              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AccountBalanceIcon className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Free Plan Limitations
                </h3>
                <p className="text-gray-600 text-sm">
                  You're on our free plan with limited features. Upgrade to unlock advanced capabilities and scale your impact.
                </p>
              </div>

              <div className="space-y-2 mb-6">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Limited Features:</h4>
                <div className="space-y-2">
                  <div className="flex items-center text-sm text-red-800">
                    <div className="w-4 h-4 rounded-full bg-red-500 mr-3 flex-shrink-0"></div>
                    <span>No canvasser seats or shift tracking</span>
                  </div>
                  <div className="flex items-center text-sm text-red-800">
                    <div className="w-4 h-4 rounded-full bg-red-500 mr-3 flex-shrink-0"></div>
                    <span>No reminder opt-ins for voters</span>
                  </div>
                  <div className="flex items-center text-sm text-red-800">
                    <div className="w-4 h-4 rounded-full bg-red-500 mr-3 flex-shrink-0"></div>
                    <span>No advanced exports or compliance reports</span>
                  </div>
                  <div className="flex items-center text-sm text-red-800">
                    <div className="w-4 h-4 rounded-full bg-red-500 mr-3 flex-shrink-0"></div>
                    <span>No engagement logging or field summaries</span>
                  </div>
                  <div className="flex items-center text-sm text-red-800">
                    <div className="w-4 h-4 rounded-full bg-red-500 mr-3 flex-shrink-0"></div>
                    <span>Limited to &lt;1,000 registration checks</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2 mb-6">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Unlock with Upgrade:</h4>
                <div className="space-y-2">
                  <div className="flex items-center text-sm text-lime-700">
                    <div className="w-4 h-4 rounded-full bg-green-500 mr-3 flex-shrink-0"></div>
                    <span>Full canvasser workflows & shift management</span>
                  </div>
                  <div className="flex items-center text-sm text-lime-700">
                    <div className="w-4 h-4 rounded-full bg-green-500 mr-3 flex-shrink-0"></div>
                    <span>Voter reminder opt-ins & messaging</span>
                  </div>
                  <div className="flex items-center text-sm text-lime-700">
                    <div className="w-4 h-4 rounded-full bg-green-500 mr-3 flex-shrink-0"></div>
                    <span>Advanced exports & compliance reports</span>
                  </div>
                  <div className="flex items-center text-sm text-lime-700">
                    <div className="w-4 h-4 rounded-full bg-green-500 mr-3 flex-shrink-0"></div>
                    <span>Higher API limits & field analytics</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center mb-6">
                <input
                  type="checkbox"
                  id="dontShowAgain"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="dontShowAgain" className="ml-2 text-sm text-gray-600">
                  Don't show this message again
                </label>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="primary"
                  size="medium"
                  label="Upgrade Now"
                  onClick={handleUpgradeClick}
                  additionalClassname="flex-1"
                />
                <Button
                  variant="tertiary"
                  size="medium"
                  label="Close"
                  onClick={handleCloseFreePlanModal}
                  additionalClassname="flex-1"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </ShiftProvider>
  );
}
